import React, { memo, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';

import Separator from '../../../details/view/CustomFields/Separator';
import ReadOnlyFieldTooltip from '../../helpers/ReadOnlyFieldTooltip';
import AggregatedCustomAttributeField from '../fields/AggregatedCustomAttributeField';
import { aggregatedMetaFieldsPropType } from '../helpers/aggregatedFieldPropTypes';
import styles from '../../ProductMetafields/ProductMetaFields.module.scss';

const COLLAPSED_KEY_PREFIX = 'aggregatedCustomFieldGroupCollapsed:';

const AggregatedCustomFieldGroup = ({
  group,
  metaFields,
  readOnly,
  readOnlyTooltip,
  detailsPanelVisibility,
  toggleVisibility,
}) => {
  const separator = group[0];
  const collapsedKey = `${COLLAPSED_KEY_PREFIX}${separator?.title}`;
  const isVisible = !detailsPanelVisibility[collapsedKey];

  const handleToggleVisibility = useCallback(() => {
    toggleVisibility(collapsedKey);
  }, [collapsedKey, toggleVisibility]);

  const fields = useMemo(
    () => group.filter((item) => item.type !== 'separator'),
    [group],
  );

  if (!separator || separator.visibility === 'hidden') return null;

  return (
    <div
      className={cn('detailsPanel__customFields', styles.group, { act: isVisible })}
    >
      <div className="customFieldGroup">
        <Separator
          title={separator.title}
          customFieldsAllowed
          toggleVisibility={handleToggleVisibility}
        />
        <If condition={isVisible}>
          {fields.map((item) => (
            <ReadOnlyFieldTooltip
              key={item.title}
              tooltip={readOnly ? readOnlyTooltip : null}
            >
              <AggregatedCustomAttributeField
                item={item}
                field={metaFields[item.title]}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
              />
            </ReadOnlyFieldTooltip>
          ))}
        </If>
      </div>
    </div>
  );
};

AggregatedCustomFieldGroup.propTypes = {
  group: PropTypes.arrayOf(PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  )).isRequired,
  metaFields: aggregatedMetaFieldsPropType,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
};

AggregatedCustomFieldGroup.defaultProps = {
  metaFields: null,
  readOnly: false,
  readOnlyTooltip: null,
};

export default memo(AggregatedCustomFieldGroup);
